import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { ComicGroup, ComicSeries, ExternalId, SeriesInput, TrackBy } from '../types';
import {
  ComicSearchResult,
  SOURCE_NAMES,
  fetchDetail,
  hasDetail,
  isCollectedType,
  trackByForType,
} from '../comicSearch';
import CoverImage from './CoverImage';
import ComicSearchField from './ComicSearchField';

interface SeriesFormModalProps {
  open: boolean;
  // null = adding
  series: ComicSeries | null;
  groups: ComicGroup[];
  onClose: () => void;
  onSave: (input: SeriesInput) => Promise<boolean>;
}

const parseInteger = (value: string) => {
  const parsed = parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const SeriesFormModal = ({ open, series, groups, onClose, onSave }: SeriesFormModalProps) => {
  const [title, setTitle] = useState('');
  const [startYear, setStartYear] = useState('');
  const [volumeNumber, setVolumeNumber] = useState('');
  const [publisher, setPublisher] = useState('');
  const [groupId, setGroupId] = useState<string | null>(null);
  const [trackBy, setTrackBy] = useState<TrackBy>('volume');
  const [totalCount, setTotalCount] = useState('');
  const [ongoing, setOngoing] = useState(false);
  const [wishlisted, setWishlisted] = useState(false);
  const [coverUrl, setCoverUrl] = useState('');
  const [externalId, setExternalId] = useState<ExternalId | null>(null);
  const [filledNote, setFilledNote] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const sortedGroups = useMemo(
    () => [...groups].sort((a, b) => a.sortOrder - b.sortOrder),
    [groups]
  );

  useEffect(() => {
    if (!open) return;
    setTitle(series?.title ?? '');
    setStartYear(series?.startYear ? String(series.startYear) : '');
    setVolumeNumber(series?.volumeNumber ? String(series.volumeNumber) : '');
    setPublisher(series?.publisher ?? '');
    setGroupId(series?.groupId ?? null);
    setTrackBy(series?.trackBy ?? 'volume');
    setTotalCount(series?.totalCount ? String(series.totalCount) : '');
    setOngoing(series?.ongoing ?? false);
    setWishlisted(series?.wishlisted ?? false);
    setCoverUrl(series?.coverUrl ?? '');
    setExternalId(series?.externalId ?? null);
    setFilledNote(null);
    setSaving(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // A cover arrives after a pick; a newer pick makes older ones stale
  const pickToken = useRef(0);

  const handlePick = async (result: ComicSearchResult) => {
    const token = ++pickToken.current;
    setTitle(result.title);
    setStartYear(result.year ? String(result.year) : '');
    setPublisher(result.publisher ?? '');
    setCoverUrl(result.coverUrl);
    setExternalId({ source: result.source, id: result.id });

    // Metron says what kind of series it is, so the tracking can be set too
    const nextTrackBy = result.seriesType ? trackByForType(result.seriesType) : trackBy;
    const isOngoing = result.source === 'metron' && nextTrackBy === 'issue' && !result.ended;
    if (result.seriesType) {
      setTrackBy(nextTrackBy);
      setOngoing(isOngoing);
    }
    // Comic Vine only counts issues, so its count is the total only by issue
    const countIsTotal = result.source === 'metron' ? !isOngoing : nextTrackBy === 'issue';
    setTotalCount(result.count && countIsTotal ? String(result.count) : '');

    const unit = isCollectedType(result.seriesType) ? 'volumes' : 'issues';
    setFilledNote(
      `Filled from ${SOURCE_NAMES[result.source]}.` +
        (result.count ? ` ${result.count} ${unit} so far.` : '') +
        ' Check the tracking below.'
    );

    if (!hasDetail(result)) return;
    try {
      const detail = await fetchDetail(result);
      if (token !== pickToken.current) return;
      if (detail.coverUrl) setCoverUrl((current) => current || detail.coverUrl || '');
      if (detail.publisher) setPublisher((current) => current || detail.publisher || '');
    } catch (err) {
      console.error(err);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    const saved = await onSave({
      title: title.trim(),
      startYear: parseInteger(startYear),
      volumeNumber: parseInteger(volumeNumber),
      publisher: publisher.trim(),
      groupId,
      trackBy,
      totalCount: parseInteger(totalCount),
      ongoing,
      coverUrl: coverUrl.trim(),
      externalId,
      wishlisted,
    });
    setSaving(false);
    if (saved) onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{series ? 'Edit Series' : 'New Series'}</DialogTitle>

      <DialogContent dividers>
        {!series && (
          <Box sx={{ mb: 2.5 }}>
            <ComicSearchField kinds="volume" placeholder="Search for a series" onPick={handlePick} />
            {filledNote && (
              <Alert severity="success" sx={{ mt: 1.5 }} onClose={() => setFilledNote(null)}>
                {filledNote}
              </Alert>
            )}
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          <Box sx={{ width: { xs: 84, sm: 110 }, flexShrink: 0, borderRadius: 1, overflow: 'hidden' }}>
            <CoverImage src={coverUrl.trim() || undefined} title={title || 'Cover'} />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              autoFocus={Boolean(series)}
              required
              fullWidth
              placeholder="e.g. Batman"
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Start year"
                value={startYear}
                onChange={(e) => setStartYear(e.target.value)}
                type="number"
                fullWidth
              />
              <TextField
                label="Vol."
                value={volumeNumber}
                onChange={(e) => setVolumeNumber(e.target.value)}
                type="number"
                helperText="Publisher's volume"
                fullWidth
              />
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <TextField
            label="Cover image URL"
            value={coverUrl}
            onChange={(e) => setCoverUrl(e.target.value)}
            helperText="Optional. Without one, the first comic's cover is used."
            fullWidth
          />

          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <TextField
              label="Publisher"
              value={publisher}
              onChange={(e) => setPublisher(e.target.value)}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel id="series-form-group-label">Group</InputLabel>
              <Select
                labelId="series-form-group-label"
                value={groupId ?? ''}
                label="Group"
                onChange={(e) => setGroupId(e.target.value || null)}
              >
                <MenuItem value="">
                  <em>No group</em>
                </MenuItem>
                {sortedGroups.map((group) => (
                  <MenuItem key={group.id} value={group.id}>
                    {group.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
            <Box>
              <FormLabel sx={{ display: 'block', fontSize: 12, mb: 0.5 }}>Track by</FormLabel>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={trackBy}
                onChange={(_, next: TrackBy | null) => next && setTrackBy(next)}
                aria-label="Track by"
              >
                <ToggleButton value="volume" sx={{ textTransform: 'none', px: 1.5 }}>
                  Volumes
                </ToggleButton>
                <ToggleButton value="issue" sx={{ textTransform: 'none', px: 1.5 }}>
                  Issues
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <TextField
              label={trackBy === 'issue' ? 'Total issues' : 'Total volumes'}
              value={totalCount}
              onChange={(e) => setTotalCount(e.target.value)}
              type="number"
              helperText="Blank if unknown"
              sx={{ width: 150 }}
            />

            <FormControlLabel
              control={<Switch checked={ongoing} onChange={(e) => setOngoing(e.target.checked)} />}
              label="Ongoing"
            />

            <FormControlLabel
              control={
                <Switch checked={wishlisted} onChange={(e) => setWishlisted(e.target.checked)} />
              }
              label="On my Wishlist"
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={!title.trim() || saving}>
          {saving ? 'Saving…' : series ? 'Save' : 'Add Series'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SeriesFormModal;
